import { updateRecord } from '@ms/yammer-data-support';

import type {
  FileUploadJobCompletedAction,
  FileUploadJobFailedAction,
  FileUploadJobStartedAction,
} from '../../domains/fileUploadJobs/actions.types';
import type {
  PublisherAttachmentAddedAction,
  PublisherAttachmentsRemovedAction,
  PublisherDismissedLinksRemovedAction,
  PublisherGifPickerClosedAction,
  PublisherGifPickerOpenedAction,
  PublisherInlineAttachmentsChangedAction,
  PublisherLinksDismissedAction,
  PublisherOverLimitAttachmentAttemptedAction,
} from '../../domains/publishers/actions.types';
import type {
  ResolveWebLinkFulfilledAction,
  ResolveWebLinkPendingAction,
  ResolveWebLinkRejectedAction,
} from '../../domains/resolveWebLink/actions.types';
import type { Publishers } from '../types';

import {
  addAttachmentToPublisher,
  removeAttachmentsFromPublisher,
  updateAttachmentReference,
  updateInlineAttachmentIds,
} from './attachments';
import type { PublisherAttachmentReference } from './attachments.types';
import type { PublishersAction } from './reducer.types';
import { isEditPublisher } from './types';
import type { Publisher } from './types';

const openGifPicker = (state: Publishers, { id }: PublisherGifPickerOpenedAction['payload']) =>
  updateRecord(state, id, { isGifPickerOpen: true });

const closeGifPicker = (state: Publishers, { id }: PublisherGifPickerClosedAction['payload']) =>
  updateRecord(state, id, { isGifPickerOpen: false });

const appendUnique = (existing: readonly string[] = [], toAdd: readonly string[]): string[] => {
  const newUrls = toAdd.filter((url, index) => !existing.includes(url) && toAdd.indexOf(url) === index);

  return [...existing, ...newUrls];
};

const withoutUrls = (existing: readonly string[] = [], toRemove: readonly string[]): string[] =>
  existing.filter((url) => !toRemove.includes(url));

const dismissLinks = (state: Publishers, { id, urls }: PublisherLinksDismissedAction['payload']) =>
  updateRecord(state, id, {
    dismissedLinkUrls: appendUnique(state[id].dismissedLinkUrls, urls),
  });

const clearDismissedLinks = (state: Publishers, { id, urls }: PublisherDismissedLinksRemovedAction['payload']) =>
  updateRecord(state, id, { dismissedLinkUrls: withoutUrls(state[id].dismissedLinkUrls, urls) });

const setOverLimitAttachmentError = (
  state: Publishers,
  { id, count }: PublisherOverLimitAttachmentAttemptedAction['payload']
) => updateRecord(state, id, { overLimitAttachmentCount: count });

const clearOverLimitAttachmentError = (state: Publishers, publisherId: Publisher['id']): Publishers =>
  updateRecord(state, publisherId, { overLimitAttachmentCount: 0 });

const addAttachment = (state: Publishers, payload: PublisherAttachmentAddedAction['payload']) => {
  const { id: publisherId, attachmentReference, isHeaderImage } = payload;

  return addAttachmentToPublisher({
    state,
    publisherId,
    attachmentToAdd: attachmentReference,
    isHeaderImage,
  });
};

const removeAttachments = (state: Publishers, payload: PublisherAttachmentsRemovedAction['payload']) => {
  const { id: publisherId, attachmentReferences } = payload;

  const stateWithoutAttachments = removeAttachmentsFromPublisher({
    state,
    publisherId,
    attachmentsToRemove: attachmentReferences,
  });

  return clearOverLimitAttachmentError(stateWithoutAttachments, publisherId);
};

const inlineAttachmentsChanged = (state: Publishers, payload: PublisherInlineAttachmentsChangedAction['payload']) => {
  const { publisherId, inlineAttachmentIds, convertedInlineAttachmentPlaceholderIds = [] } = payload;
  const stateWithInlineAttachmentIds = updateInlineAttachmentIds({ state, publisherId, inlineAttachmentIds });
  const publisher = stateWithInlineAttachmentIds[publisherId];

  if (convertedInlineAttachmentPlaceholderIds.length === 0) {
    return stateWithInlineAttachmentIds;
  }

  return updateRecord(stateWithInlineAttachmentIds, publisherId, {
    attachments: publisher.attachments.map((attachment) => {
      if (
        !('editorPlaceholderId' in attachment) ||
        !attachment.editorPlaceholderId ||
        !convertedInlineAttachmentPlaceholderIds.includes(attachment.editorPlaceholderId)
      ) {
        return attachment;
      }

      const { editorPlaceholderId, ...galleryAttachment } = attachment;

      return galleryAttachment;
    }),
  });
};

const addResolvingWeblinkToPublisher = (
  state: Publishers,
  payload: ResolveWebLinkPendingAction['payload']
): Publishers => {
  const {
    link,
    publisherId,
    attachedViaMRU,
    isAttachedViaFilePicker,
    isMedia,
    gifType,
    isAttachmentsEditEnabled,
    isHeaderImage,
    isResolvingWebLinksInline,
  } = payload;
  const isPublisherEdit = state[publisherId] ? isEditPublisher(state[publisherId]) : false;

  if (isPublisherEdit && !isAttachmentsEditEnabled) {
    return state;
  }

  const attachmentToAdd: PublisherAttachmentReference = isResolvingWebLinksInline
    ? { id: link, schema: 'resolvingWebLinksInline' }
    : {
        id: link,
        schema: 'resolvingWebLinks',
        ...(attachedViaMRU !== undefined && {
          attachedViaMRU,
          isAttachedViaFilePicker: isAttachedViaFilePicker ?? false,
          ...(isMedia === true && { isMedia: true }),
        }),
        ...(gifType !== undefined && { gifType }),
      };

  return addAttachmentToPublisher({
    state,
    publisherId,
    attachmentToAdd,
    isHeaderImage,
  });
};

const removeRejectedWebLinkFromPublisher = (state: Publishers, payload: ResolveWebLinkRejectedAction['payload']) => {
  const { link, publisherId } = payload;

  return removeAttachmentsFromPublisher({
    state,
    publisherId,
    attachmentsToRemove: [
      {
        id: link,
        schema: 'resolvingWebLinks',
      },
    ],
  });
};

const resolveWebLinkOnPublisher = (
  state: Publishers,
  payload: ResolveWebLinkFulfilledAction['payload']
): Publishers => {
  const {
    link: webLinkUrl,
    publisherId,
    attachedViaMRU,
    isAttachedViaFilePicker,
    isLocked,
    gifType,
    result: { id: webLinkId, schema },
    isAttachmentsEditEnabled,
    isResolvingWebLinksInline,
    editorPlaceholderId,
    tags,
  } = payload;

  const isPublisherEdit = state[publisherId] ? isEditPublisher(state[publisherId]) : false;

  if (isPublisherEdit && !isAttachmentsEditEnabled) {
    return state;
  }

  const resolvedAttachment: PublisherAttachmentReference = (() => {
    switch (schema) {
      case 'webLinks':
      case 'sharePointFileLinks':
      case 'sharePointVideoLinks':
      case 'sharePointWebLinks':
        return { id: webLinkId, schema, attachedViaMRU, isAttachedViaFilePicker, isLocked };
      case 'webImages':
        return { id: webLinkId, schema, isLocked, gifType, editorPlaceholderId, tags };
      case 'webVideos':
        return { id: webLinkId, schema, isLocked };
      case 'resolvingWebLinks':
        return { id: webLinkId, schema, attachedViaMRU, isAttachedViaFilePicker, gifType };
      case 'resolvingWebLinksInline':
        return { id: webLinkId, schema };
    }
  })();

  return updateAttachmentReference({
    state,
    publisherId,
    attachmentToUpdate: {
      id: webLinkUrl,
      schema: isResolvingWebLinksInline ? 'resolvingWebLinksInline' : 'resolvingWebLinks',
    },
    updatedAttachment: resolvedAttachment,
  });
};

const attachFileUploadToPublisher = (state: Publishers, payload: FileUploadJobStartedAction['payload']): Publishers => {
  const { id, publisherId, editorPlaceholderId, isHeaderImage } = payload;

  return addAttachmentToPublisher({
    state,
    publisherId,
    attachmentToAdd: {
      id,
      schema: 'fileUploadJobs',
      editorPlaceholderId,
    },
    isHeaderImage,
  });
};

const promoteFileUploadCompletedOnPublisher = (
  state: Publishers,
  payload: FileUploadJobCompletedAction['payload']
): Publishers => {
  const {
    id: fileUploadJobId,
    publisherId,
    result: { id: resolvedFileId, schema },
    isHeaderImage,
    editorPlaceholderId,
  } = payload;

  return updateAttachmentReference({
    state,
    publisherId,
    attachmentToUpdate: { id: fileUploadJobId, schema: 'fileUploadJobs' },
    updatedAttachment: {
      id: resolvedFileId,
      schema,
      wasAttachedViaUpload: true,
      editorPlaceholderId,
      fileUploadJobId,
    },
    isHeaderImage,
  });
};

const removeFailedFileUpload = (state: Publishers, payload: FileUploadJobFailedAction['payload']): Publishers => {
  const { id, publisherId } = payload;

  return removeAttachmentsFromPublisher({
    state,
    publisherId,
    attachmentsToRemove: [{ id, schema: 'fileUploadJobs' }],
  });
};

const updateHasRemovedAttachmentsNotDeleted = (
  state: Publishers,
  publisherId: Publisher['id'] | undefined
): Publishers =>
  publisherId ? updateRecord(state, publisherId, { hasRemovedAttachmentsNotDeletedFromSharePoint: true }) : state;

const toggleAttemptedToAttachFileToPublisher = (state: Publishers, publisherId: Publisher['id']) => {
  const publisher = state[publisherId];

  const updatedPublisherFields = {
    hasAttemptedToAttachFile: !publisher.hasAttemptedToAttachFile,
  };

  return updateRecord(state, publisherId, updatedPublisherFields);
};

type AttachmentActionsReducer = (state: Publishers, action: PublishersAction) => Publishers;
export const attachmentActionsReducer: AttachmentActionsReducer = (state, action) => {
  switch (action.type) {
    case 'PUBLISHER.GIF_PICKER_OPENED':
      return openGifPicker(state, action.payload);
    case 'PUBLISHER.GIF_PICKER_CLOSED':
      return closeGifPicker(state, action.payload);
    case 'PUBLISHER.LINKS_DISMISSED':
      return dismissLinks(state, action.payload);
    case 'PUBLISHER.DISMISSED_LINKS_REMOVED':
      return clearDismissedLinks(state, action.payload);
    case 'PUBLISHER.OVER_LIMIT_ATTACHMENT_ATTEMPTED':
      return setOverLimitAttachmentError(state, action.payload);
    case 'PUBLISHER.ATTACHMENT_ADDED':
      return addAttachment(state, action.payload);
    case 'PUBLISHER.ATTACHMENTS_REMOVED':
      return removeAttachments(state, action.payload);
    case 'PUBLISHER.INLINE_ATTACHMENTS_CHANGED':
      return inlineAttachmentsChanged(state, action.payload);
    case 'WEBLINK.RESOLVE_PENDING':
      return addResolvingWeblinkToPublisher(state, action.payload);
    case 'WEBLINK.RESOLVE_FULFILLED':
      return resolveWebLinkOnPublisher(state, action.payload);
    case 'WEBLINK.RESOLVE_REJECTED':
      return removeRejectedWebLinkFromPublisher(state, action.payload);
    case 'FILE_UPLOAD_JOB.STARTED':
      return attachFileUploadToPublisher(state, action.payload);
    case 'FILE_UPLOAD_JOB.COMPLETED':
      return promoteFileUploadCompletedOnPublisher(state, action.payload);
    case 'FILE_UPLOAD_JOB.FAILED':
      return removeFailedFileUpload(state, action.payload);
    case 'PUBLISHER.MARK_EXISTING_FILE_REMOVED':
      return updateRecord(state, action.payload.publisherId, { hasRemovedAttachmentsNotDeletedFromSharePoint: true });
    case 'PUBLISHER.TOGGLE_ATTEMPTED_TO_ATTACH_FILE':
      return toggleAttemptedToAttachFileToPublisher(state, action.payload.publisherId);
    case 'FILE.DELETE_REJECTED':
      return updateHasRemovedAttachmentsNotDeleted(state, action.payload.publisherId);
    default:
      return state;
  }
};
